import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Seo from "../../components/Seo/Seo";
import CustomButton from "../../components/CustomButton/CustomButton";
import GoogleSignInModal from "../../components/GoogleSignInModal/GoogleSignInModal";
import ContactUsModal from "../../components/ContactUsModal/ContactUsModal";
import CaptionProfileMenu from "../../components/CaptionProfileMenu/CaptionProfileMenu";
import CaptionPaymentModal from "../../components/CaptionPaymentModal/CaptionPaymentModal";
import CaptionPublishingHistory from "../../components/CaptionPublishingHistory/CaptionPublishingHistory";
import GoogleLoginHistoryPanel from "../../components/GoogleLoginHistoryPanel/GoogleLoginHistoryPanel";
import { PAGE_SEO } from "../../config/seoConfig";
import { useAuth } from "../../hooks/useAuth";
import {
  CAPTION_UPLOAD_PHASE_MESSAGES,
  createCaptionJob,
  fetchCaptionJob,
  fetchCaptionJobVideo,
  fetchMyCaptionJobs,
  fetchMyCaptionUsage,
  formatCaptionUsageSummary,
  getCaptionApiErrorMessage,
  pollCaptionJob,
} from "../../services/captionPublishingApi";
import {
  buildLatinTranscriptText,
  buildPhoneticTranscriptText,
  buildSrtContent,
  buildTranscriptText,
  buildVttContent,
  downloadTextFile,
  getPhoneticDownloadBaseName,
  getSubtitleBaseName,
  normalizeLatinSegments,
  normalizePhoneticSegments,
  toLatinScriptText,
  validateVideoDuration,
  validateVideoFile,
  formatVideoDuration,
  NO_AUDIO_DETECTED_MESSAGE,
  isNoAudioTranscript,
} from "../../util/subtitleHelpers";
import {
  resolveCaptionStatusMessage,
} from "../../util/captionPublishingErrors";
import { recordConsumptionAlert } from "../../util/captionConsumptionAlerts";
import "./CaptionPublishing.scss";
import CaptionPublishingLanding from "./CaptionPublishingLanding";

const STEPS = [
  { id: "upload", label: "Upload Video" },
  { id: "preview", label: "Preview" },
  { id: "process", label: "Generate transcript" },
  { id: "download", label: "Download Subtitles" },
];

const formatFileSize = (bytes) => {
  if (bytes < 1024 * 1024) {
    return `${(bytes / 1024).toFixed(1)} KB`;
  }

  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const CaptionPublishing = () => {
  const { isLoggedIn, user, logout } = useAuth();
  const fileInputRef = useRef(null);
  const previewUrlRef = useRef("");
  const previewSourceRef = useRef(null);

  const [videoFile, setVideoFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [historyVideoName, setHistoryVideoName] = useState("");
  const [isHistoryVideoLoading, setIsHistoryVideoLoading] = useState(false);
  const [historyJobs, setHistoryJobs] = useState([]);
  const [selectedJobId, setSelectedJobId] = useState("");
  const [isHistoryLoading, setIsHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusType, setStatusType] = useState("info");
  const [requiresPaidSubscription, setRequiresPaidSubscription] = useState(false);
  const [segments, setSegments] = useState([]);
  const [transcript, setTranscript] = useState("");
  const [phoneticSegments, setPhoneticSegments] = useState([]);
  const [phoneticTranscript, setPhoneticTranscript] = useState("");
  const [activeStep, setActiveStep] = useState("upload");
  const [noAudioDetected, setNoAudioDetected] = useState(false);
  const [usageSummary, setUsageSummary] = useState("");
  const [videoDurationSeconds, setVideoDurationSeconds] = useState(null);
  const [transliterationFormat, setTransliterationFormat] = useState("");
  const [nativeScriptFormat, setNativeScriptFormat] = useState("");
  const [showContactModal, setShowContactModal] = useState(false);
  const [showSignInModal, setShowSignInModal] = useState(false);
  const [showPricingSection, setShowPricingSection] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedPaymentPlan, setSelectedPaymentPlan] = useState(null);
  const pendingAuthActionRef = useRef(null);
  const shouldScrollToPricingRef = useRef(false);

  const clearTranscriptState = useCallback(() => {
    setSegments([]);
    setTranscript("");
    setPhoneticSegments([]);
    setPhoneticTranscript("");
    setTransliterationFormat("");
    setNativeScriptFormat("");
  }, []);

  const showNoAudioError = useCallback(() => {
    setNoAudioDetected(true);
    clearTranscriptState();
    setActiveStep("preview");
    setRequiresPaidSubscription(false);
    setStatusType("error");
    setStatusMessage(NO_AUDIO_DETECTED_MESSAGE);
  }, [clearTranscriptState]);

  const showCaptionStatus = useCallback((type, message, error = null) => {
    const resolved = resolveCaptionStatusMessage(
      error ? getCaptionApiErrorMessage(error, message) : message,
      error,
    );

    setRequiresPaidSubscription(resolved.requiresPaidSubscription);
    setStatusType(type);
    setStatusMessage(resolved.message);

    if (resolved.requiresPaidSubscription && user?.email) {
      recordConsumptionAlert(user, {
        reason: resolved.consumptionReason,
        message: resolved.message,
        page: "/captionPublishing",
      });
    }
  }, [user]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Do not revoke blob URLs on unmount — React Strict Mode and step changes
  // were revoking preview URLs while upload/processing still needed them.

  // React Strict Mode remounts after cleanup revokes the blob URL while state
  // still holds the old src — recreate from the stored File/Blob when needed.
  useEffect(() => {
    if (!previewSourceRef.current || previewUrlRef.current) {
      return;
    }

    const objectUrl = URL.createObjectURL(previewSourceRef.current);
    previewUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
  }, [previewUrl]);

  const openContactModal = useCallback(() => {
    setShowContactModal(true);
  }, []);

  const closeContactModal = useCallback(() => {
    setShowContactModal(false);
  }, []);

  const runPendingAuthAction = useCallback(() => {
    const pending = pendingAuthActionRef.current;
    pendingAuthActionRef.current = null;

    if (pending) {
      pending();
    }
  }, []);

  const requestAuth = useCallback(
    (action) => {
      if (isLoggedIn) {
        if (action) {
          action();
        }
        return;
      }

      pendingAuthActionRef.current = action || null;
      setShowSignInModal(true);
    },
    [isLoggedIn],
  );

  const handleProfileSignInRequest = useCallback(() => {
    requestAuth(null);
  }, [requestAuth]);

  const openPricing = useCallback(() => {
    shouldScrollToPricingRef.current = true;
    setShowPricingSection(true);
  }, []);

  const openFilePicker = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleUploadRequest = useCallback(() => {
    requestAuth(openFilePicker);
  }, [requestAuth, openFilePicker]);

  const handleGetStartedPlan = useCallback(
    (plan) => {
      requestAuth(() => {
        setSelectedPaymentPlan(plan);
        setShowPaymentModal(true);
      });
    },
    [requestAuth],
  );

  const handleFreePlanClick = useCallback(() => {
    const scrollToTool = () => {
      document.getElementById("caption-tool")?.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    if (isLoggedIn) {
      scrollToTool();
      return;
    }

    requestAuth(scrollToTool);
  }, [isLoggedIn, requestAuth]);

  useEffect(() => {
    if (!showPricingSection || !shouldScrollToPricingRef.current) {
      return undefined;
    }

    shouldScrollToPricingRef.current = false;
    const timer = window.setTimeout(() => {
      document.getElementById("caption-pricing")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 120);

    return () => window.clearTimeout(timer);
  }, [showPricingSection]);

  const applyJobData = useCallback((job) => {
    const nextSegments = job?.segments || [];
    const nextTranscript = job?.transcript || buildTranscriptText(nextSegments);
    const nextPhoneticSegments = job?.phoneticSegments || job?.devanagariSegments || [];
    const nextPhoneticTranscript =
      job?.phoneticTranscript ||
      job?.devanagariTranscript ||
      buildPhoneticTranscriptText(nextPhoneticSegments);

    setSelectedJobId(job?.jobId || "");

    if (isNoAudioTranscript(nextTranscript, nextSegments)) {
      setNoAudioDetected(true);
      clearTranscriptState();
      setActiveStep("preview");
      setStatusType("error");
      setStatusMessage(NO_AUDIO_DETECTED_MESSAGE);
      return;
    }

    setNoAudioDetected(false);
    setSegments(nextSegments);
    setTranscript(nextTranscript);
    setPhoneticSegments(nextPhoneticSegments);
    setPhoneticTranscript(nextPhoneticTranscript);
    setActiveStep(nextTranscript || nextSegments.length ? "download" : "upload");
  }, [clearTranscriptState]);

  const loadHistory = useCallback(async ({ force = false } = {}) => {
    if (!force && !isLoggedIn) {
      setHistoryJobs([]);
      return;
    }

    setIsHistoryLoading(true);
    setHistoryError("");

    try {
      const items = await fetchMyCaptionJobs();
      setHistoryJobs(items);
    } catch (error) {
      setHistoryJobs([]);
      setHistoryError(
        getCaptionApiErrorMessage(error, "Unable to load your caption upload history."),
      );
    } finally {
      setIsHistoryLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  const loadUsage = useCallback(async () => {
    if (!isLoggedIn) {
      setUsageSummary("");
      return;
    }

    try {
      const usage = await fetchMyCaptionUsage();
      setUsageSummary(formatCaptionUsageSummary(usage));
    } catch {
      setUsageSummary("");
    }
  }, [isLoggedIn]);

  useEffect(() => {
    loadUsage();
  }, [loadUsage]);

  const handleSignInSuccess = useCallback(() => {
    loadHistory({ force: true });
    loadUsage();
    setShowSignInModal(false);
    runPendingAuthAction();
  }, [loadHistory, loadUsage, runPendingAuthAction]);

  const resetPreviewUrl = () => {
    setPreviewUrl("");

    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = "";
    }

    previewSourceRef.current = null;
  };

  const setPreviewFromSource = (source) => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
    }

    previewSourceRef.current = source;
    const objectUrl = URL.createObjectURL(source);
    previewUrlRef.current = objectUrl;
    setPreviewUrl(objectUrl);
  };

  const loadJobVideoPreview = async (jobId, videoFileName) => {
    resetPreviewUrl();
    setPreviewUrl("");
    setIsHistoryVideoLoading(true);

    try {
      const blob = await fetchCaptionJobVideo(jobId);
      setPreviewFromSource(blob);
      setHistoryVideoName(videoFileName || "Previous upload");
      return true;
    } finally {
      setIsHistoryVideoLoading(false);
    }
  };

  const handleSelectHistoryJob = async (job) => {
    try {
      setVideoFile(null);
      setHistoryVideoName("");
      setVideoDurationSeconds(null);
      resetPreviewUrl();
      setPreviewUrl("");

      const details = await fetchCaptionJob(job.jobId);
      const merged = { ...job, ...details };
      applyJobData(merged);

      const detectedNoAudio = isNoAudioTranscript(
        merged.transcript || buildTranscriptText(merged.segments || []),
        merged.segments || [],
      );

      try {
        await loadJobVideoPreview(
          job.jobId,
          details.videoFileName || job.videoFileName || job.title,
        );

        if (!detectedNoAudio) {
          setActiveStep(
            merged.transcript || merged.segments?.length ? "download" : "preview",
          );
          setStatusType("success");
          setStatusMessage("Loaded a previous upload from your history.");
        }
      } catch (videoError) {
        if (!detectedNoAudio) {
          setActiveStep(
            merged.transcript || merged.segments?.length ? "download" : "upload",
          );
          setStatusType("success");
          setStatusMessage(
            `${getCaptionApiErrorMessage(
              videoError,
              "Video preview unavailable for this upload.",
            )} Transcript loaded from history.`,
          );
        }
      }
    } catch (error) {
      showCaptionStatus("error", "Unable to load selected upload.", error);
    }
  };

  const handleCreateNew = () => {
    setSelectedJobId("");
    resetPreviewUrl();
    setPreviewUrl("");
    setVideoFile(null);
    setHistoryVideoName("");
    setVideoDurationSeconds(null);
    setNoAudioDetected(false);
    clearTranscriptState();
    setProgress(0);
    setActiveStep("upload");
    setRequiresPaidSubscription(false);
    setStatusType("info");
    setStatusMessage("Start a new caption upload.");
  };

  const handleVideoSelection = async (file) => {
    const validationError = validateVideoFile(file);

    if (validationError) {
      showCaptionStatus("error", validationError);
      return;
    }

    const durationError = await validateVideoDuration(file);

    if (durationError.error) {
      showCaptionStatus("error", durationError.error);
      return;
    }

    resetPreviewUrl();
    setPreviewFromSource(file);

    setVideoFile(file);
    setVideoDurationSeconds(durationError.durationSeconds);
    setHistoryVideoName("");
    setNoAudioDetected(false);
    clearTranscriptState();
    setProgress(0);
    setActiveStep("preview");
    setRequiresPaidSubscription(false);
    setStatusType("success");
    setStatusMessage("Video uploaded. Preview it below, then generate subtitles.");
  };

  const onFileInputChange = (event) => {
    const file = event.target.files?.[0];
    if (file) {
      handleVideoSelection(file);
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setIsDragging(false);
    const file = event.dataTransfer.files?.[0];

    if (!file) {
      return;
    }

    requestAuth(() => {
      handleVideoSelection(file);
    });
  };

  const onGenerateSubtitles = async () => {
    if (!videoFile) {
      setStatusType("error");
      setStatusMessage("Upload a video before generating subtitles.");
      return;
    }

    setIsProcessing(true);
    setActiveStep("process");
    setProgress(5);
    setNoAudioDetected(false);
    setRequiresPaidSubscription(false);
    setStatusType("info");
    setStatusMessage("Uploading video and starting speech transcription...");

    try {
      const job = await createCaptionJob(videoFile, {
        language: "hinglish",
        title: videoFile.name,
        durationSeconds: videoDurationSeconds,
        onUploadPhase: (phase) => {
          setStatusMessage(
            CAPTION_UPLOAD_PHASE_MESSAGES[phase] ||
              CAPTION_UPLOAD_PHASE_MESSAGES.uploading_direct,
          );

          if (phase === "uploading_to_storage") {
            setProgress(15);
          }

          if (phase === "completing_upload") {
            setProgress(25);
          }
        },
      });

      const completedJob = await pollCaptionJob(job.jobId || job.id, {
        intervalMs: 3000,
        onUpdate: (latestJob) => {
          setProgress(Math.max(5, Number(latestJob.progress) || 0));
          setStatusMessage(
            latestJob.message || "Processing audio and generating default language transcript...",
          );
        },
      });

      if (completedJob.status === "failed") {
        const failureMessage =
          completedJob.error || completedJob.message || "Caption generation failed.";

        if (
          failureMessage.includes("Could not extract audio") ||
          failureMessage.includes("Downloaded video is incomplete") ||
          failureMessage.includes("ffmpeg binary not found")
        ) {
          showCaptionStatus("error", failureMessage);
          setActiveStep("preview");
          return;
        }

        if (
          failureMessage.includes("No audio detected") ||
          isNoAudioTranscript(completedJob.transcript, completedJob.segments)
        ) {
          showNoAudioError();
          return;
        }

        throw new Error(failureMessage);
      }

      const nextSegments = completedJob.segments || [];
      const nextTranscript =
        completedJob.transcript || buildTranscriptText(nextSegments);
      const nextPhoneticSegments =
        completedJob.phoneticSegments || completedJob.devanagariSegments || [];
      const nextPhoneticTranscript =
        completedJob.phoneticTranscript ||
        completedJob.devanagariTranscript ||
        buildPhoneticTranscriptText(nextPhoneticSegments);

      if (isNoAudioTranscript(nextTranscript, nextSegments)) {
        showNoAudioError();
        return;
      }

      setNoAudioDetected(false);
      setSegments(nextSegments);
      setTranscript(nextTranscript);
      setPhoneticSegments(nextPhoneticSegments);
      setPhoneticTranscript(nextPhoneticTranscript);
      setSelectedJobId(completedJob.jobId || job.jobId || job.id || "");
      setProgress(100);
      setActiveStep("download");
      setRequiresPaidSubscription(false);
      setStatusType("success");
      setStatusMessage(
        nextPhoneticTranscript
          ? "Phonetic script and video script are ready. Use the download buttons in sections 3 and 4."
          : "Phonetic script is ready. Video script will appear once the API returns phoneticSegments.",
      );
      await loadHistory();
      await loadUsage();
    } catch (error) {
      showCaptionStatus(
        "error",
        "Unable to generate subtitles. Please try again or contact support if the issue continues.",
        error,
      );
      setActiveStep("preview");
    } finally {
      setIsProcessing(false);
    }
  };

  const resolveDownloadSegments = (sourceSegments, sourceTranscript) => {
    if (sourceSegments.length > 0) {
      return sourceSegments;
    }

    return sourceTranscript
      .split("\n")
      .filter(Boolean)
      .map((text, index) => ({
        startMs: index * 3000,
        endMs: (index + 1) * 3000,
        text,
      }));
  };

  const onDownloadSubtitle = (format = "srt") => {
    if (!latinSegments.length && !latinTranscript.trim()) {
      setStatusType("error");
      setStatusMessage("Generate subtitles before downloading.");
      return;
    }

    const baseName = getSubtitleBaseName(videoFile?.name || historyVideoName || "video");
    const resolvedSegments = resolveDownloadSegments(latinSegments, latinTranscript);

    if (format === "vtt") {
      downloadTextFile(`${baseName}.vtt`, buildVttContent(resolvedSegments), "text/vtt");
    } else {
      downloadTextFile(`${baseName}.srt`, buildSrtContent(resolvedSegments), "application/x-subrip");
    }
  };

  const onDownloadPhoneticSubtitle = (format = "srt") => {
    if (!phoneticSegments.length && !phoneticTranscript.trim()) {
      setStatusType("error");
      setStatusMessage(
        "Phonetic script is not available yet. Ask the API team to return phoneticSegments in the job response.",
      );
      return;
    }

    const baseName = getPhoneticDownloadBaseName(videoFile?.name || historyVideoName || "video");
    const resolvedSegments = normalizePhoneticSegments(
      resolveDownloadSegments(phoneticSegments, phoneticTranscript),
    );

    if (format === "vtt") {
      downloadTextFile(`${baseName}.vtt`, buildVttContent(resolvedSegments), "text/vtt");
    } else {
      downloadTextFile(
        `${baseName}.srt`,
        buildSrtContent(resolvedSegments),
        "application/x-subrip",
      );
    }
  };

  const stepStates = useMemo(() => {
    const order = STEPS.map((step) => step.id);
    const activeIndex = order.indexOf(activeStep);

    return STEPS.reduce((accumulator, step, index) => {
      accumulator[step.id] =
        index < activeIndex ? "done" : index === activeIndex ? "active" : "";
      return accumulator;
    }, {});
  }, [activeStep]);

  const latinSegments = useMemo(() => normalizeLatinSegments(segments), [segments]);

  const latinTranscript = useMemo(() => {
    const fromSegments = buildLatinTranscriptText(latinSegments);
    if (fromSegments) {
      return fromSegments;
    }

    return toLatinScriptText(transcript);
  }, [latinSegments, transcript]);

  return (
    <div className="caption-publishing">
      <Seo {...PAGE_SEO.captionPublishing} />

      <div className="caption-publishing__topbar">
        <CaptionProfileMenu
          user={user}
          isLoggedIn={isLoggedIn}
          onSignInRequest={handleProfileSignInRequest}
          onLogout={logout}
        />
      </div>

      <section className="caption-publishing__hero">
        <div className="caption-publishing__hero-inner">
          <div className="caption-publishing__hero-copy">
            <h1 className="caption-publishing__title">
              Subtitles for every language in your video
            </h1>
            <p className="caption-publishing__subtitle">
              Upload, preview, and generate YouTube-ready captions with phonetic and native-script
              tracks — the same four-step tool you use today, with a clearer layout.
            </p>
            <div className="caption-publishing__hero-actions">
              <button
                type="button"
                className="caption-publishing__hero-cta caption-publishing__hero-cta--ghost"
                onClick={openPricing}
              >
                Paid plans
              </button>
            </div>
          </div>

          <div className="caption-publishing__hero-visual" aria-hidden="true">
            <div className="caption-publishing__hero-mock">
              <div className="caption-publishing__caption-stack">
                <div className="caption-publishing__caption-bubble caption-publishing__caption-bubble--1">
                  <span className="caption-publishing__caption-flag">EN</span>
                  Where are you today?
                </div>
                <div className="caption-publishing__caption-bubble caption-publishing__caption-bubble--2">
                  <span className="caption-publishing__caption-flag">HI</span>
                  Kaha ho aap aaj?
                </div>
                <div className="caption-publishing__caption-bubble caption-publishing__caption-bubble--3">
                  <span className="caption-publishing__caption-flag">अ</span>
                  कहाँ हो आप आज?
                </div>
              </div>
              <div className="caption-publishing__hero-frame">
                <span className="caption-publishing__hero-play" aria-hidden="true">
                  ▶
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section id="caption-tool" className="caption-publishing__tool-shell">
        <div className="caption-publishing__tool-intro">
          <span className="caption-publishing__tool-eyebrow">Caption tool</span>
          <h2 className="caption-publishing__tool-title">Upload → Preview → Generate → Download</h2>
          <p className="caption-publishing__tool-text">
            {isLoggedIn
              ? "Upload a video, preview it, generate subtitles, and download .srt or .vtt files."
              : "Preview the workflow below, then sign in from the profile icon (top right) when you are ready to upload."}
          </p>
        </div>
      <div
        className={`caption-publishing__workspace${isLoggedIn ? "" : " caption-publishing__workspace--guest"}`}
      >
        {isLoggedIn && (
          <div className="caption-publishing__sidebar">
            <CaptionPublishingHistory
              jobs={historyJobs}
              selectedJobId={selectedJobId}
              onSelectJob={handleSelectHistoryJob}
              onCreateNew={handleCreateNew}
              isLoading={isHistoryLoading}
              errorMessage={historyError}
            />
            <GoogleLoginHistoryPanel mode="user" userEmail={user?.email} limit={6} />
          </div>
        )}

        <div className="caption-publishing__main">
      <div className="caption-publishing__content">
        <div
          className={`caption-publishing__card${isLoggedIn ? "" : " caption-publishing__card--guest"}`}
        >
          {!isLoggedIn && (
            <div className="caption-publishing__guest-banner">
              <div className="caption-publishing__guest-banner-copy">
                <p className="caption-publishing__guest-banner-eyebrow">Sign in required</p>
                <h3 className="caption-publishing__guest-banner-title">
                  Sign in to upload and generate captions
                </h3>
                <p className="caption-publishing__guest-banner-text">
                  Use the profile icon in the top-right corner, or the button below, to continue
                  with Google Sign-In.
                </p>
              </div>
              <button
                type="button"
                className="caption-publishing__guest-banner-btn"
                onClick={handleProfileSignInRequest}
              >
                Sign in with Google
              </button>
            </div>
          )}

          <div className="caption-publishing__steps">
            {STEPS.map((step) => (
              <div
                key={step.id}
                className={`caption-publishing__step caption-publishing__step--${stepStates[step.id] || ""}`.trim()}
              >
                {step.label}
              </div>
            ))}
          </div>

          <h2 className="caption-publishing__section-title">1. Upload video</h2>
          <p className="caption-publishing__section-text">
            Supported formats: MP4, WebM, MOV, AVI, MKV. Free plan: max 100 MB per file, 20 minutes per video, 3 hours total.
          </p>
          {usageSummary && (
            <p className="caption-publishing__usage-summary">{usageSummary}</p>
          )}

          <div
            className={`caption-publishing__upload-zone${
              isDragging ? " caption-publishing__upload-zone--dragging" : ""
            }${isLoggedIn ? "" : " caption-publishing__upload-zone--guest"}`}
            onClick={handleUploadRequest}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            role="button"
            tabIndex={0}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") {
                handleUploadRequest();
              }
            }}
          >
            <div className="caption-publishing__upload-icon">&#9654;</div>
            {!isLoggedIn ? (
              <>
                <p className="caption-publishing__upload-title">Sign in to upload your video</p>
                <p className="caption-publishing__upload-hint">
                  Click here or use the profile icon (top right) to sign in with Google first.
                </p>
                <span className="caption-publishing__upload-formats">
                  MP4, WebM, MOV, AVI, MKV supported after sign-in
                </span>
              </>
            ) : (
              <>
                <p>Drag and drop a video here, or click to browse</p>
                {videoFile && (
                  <>
                    <p className="caption-publishing__file-name">{videoFile.name}</p>
                    <p className="caption-publishing__file-meta">
                      <span>{formatFileSize(videoFile.size)}</span>
                      {videoDurationSeconds != null && (
                        <>
                          <span className="caption-publishing__file-meta-separator">·</span>
                          <span>{formatVideoDuration(videoDurationSeconds)} playable</span>
                        </>
                      )}
                    </p>
                  </>
                )}
              </>
            )}
          </div>

          <input
            ref={fileInputRef}
            className="caption-publishing__hidden-input"
            type="file"
            accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/x-matroska,.mp4,.webm,.mov,.avi,.mkv"
            onChange={onFileInputChange}
          />

          {previewUrl && (
            <>
              <h2 className="caption-publishing__section-title">2. Preview video</h2>
              {(videoFile || historyVideoName) && (
                <>
                  <p className="caption-publishing__file-name">
                    {videoFile?.name || historyVideoName}
                  </p>
                  {videoFile && (
                    <p className="caption-publishing__file-meta">
                      <span>{formatFileSize(videoFile.size)}</span>
                      {videoDurationSeconds != null && (
                        <>
                          <span className="caption-publishing__file-meta-separator">·</span>
                          <span>{formatVideoDuration(videoDurationSeconds)} playable</span>
                        </>
                      )}
                    </p>
                  )}
                </>
              )}
              <video
                key={previewUrl}
                className="caption-publishing__video-preview"
                src={previewUrl}
                controls
                playsInline
              />
            </>
          )}

          {isHistoryVideoLoading && (
            <p className="caption-publishing__section-text">Loading video preview...</p>
          )}

          {!previewUrl && selectedJobId && !isHistoryVideoLoading && (
            <p className="caption-publishing__section-text">
              Video preview is not available for this history item.
            </p>
          )}

          <div className="caption-publishing__actions">
            <CustomButton
              title={isProcessing ? "Generating..." : "Generate Subtitles"}
              className="submitBtn"
              onClick={onGenerateSubtitles}
              disabled={!isLoggedIn || !videoFile || isProcessing}
            />
            {videoFile && (
              <CustomButton
                title="Choose Another Video"
                className="submitBtn"
                onClick={handleUploadRequest}
                disabled={isProcessing}
              />
            )}
          </div>

          {isProcessing && (
            <div className="caption-publishing__progress">
              <div
                className="caption-publishing__progress-bar"
                style={{ width: `${Math.min(progress, 100)}%` }}
              />
            </div>
          )}

        {(noAudioDetected || statusMessage) && (
            <div
              className={`caption-publishing__status caption-publishing__status--${noAudioDetected ? "error" : statusType}${noAudioDetected ? " caption-publishing__status--no-audio" : ""}`}
            >
              {noAudioDetected ? (
                <strong>{NO_AUDIO_DETECTED_MESSAGE}</strong>
              ) : (
                <>
                  {statusMessage}
                  {requiresPaidSubscription && (
                    <>
                      {" "}
                      <button
                        type="button"
                        className="caption-publishing__subscription-link"
                        onClick={openPricing}
                      >
                        View paid subscription options
                      </button>
                    </>
                  )}
                </>
              )}
            </div>
          )}
        </div>

        {noAudioDetected && (
          <div className="caption-publishing__card">
            <p className="caption-publishing__no-audio">
              <strong>{NO_AUDIO_DETECTED_MESSAGE}</strong>
            </p>
          </div>
        )}

        {!noAudioDetected && (transcript || segments.length > 0) && (
          <div className="caption-publishing__card">
            <div className="caption-publishing__section-header">
              <h2 className="caption-publishing__section-title">3. Phonetic script</h2>
              <div className="caption-publishing__format-toggle caption-publishing__format-toggle--inline">
                <button
                  type="button"
                  className={`caption-publishing__format-btn${
                    transliterationFormat === "srt" ? " caption-publishing__format-btn--active" : ""
                  }`}
                  onClick={() => {
                    setTransliterationFormat("srt");
                    onDownloadSubtitle("srt");
                  }}
                >
                  .SRT (SubRip)
                </button>
                <button
                  type="button"
                  className={`caption-publishing__format-btn${
                    transliterationFormat === "vtt" ? " caption-publishing__format-btn--active" : ""
                  }`}
                  onClick={() => {
                    setTransliterationFormat("vtt");
                    onDownloadSubtitle("vtt");
                  }}
                >
                  .VTT (WebVTT)
                </button>
              </div>
            </div>
            <p className="caption-publishing__section-text">
              Transliteration (Latin script / Hinglish) from the Devanagari script — English alphabet
              only. Example: &quot;aaj hamlog ki baat nahi ho paeege jaan&quot;, &quot;kyun nahi ho
              paegi baat&quot;.
            </p>

            <textarea
              className="caption-publishing__transcript"
              value={latinTranscript}
              readOnly
              aria-label="Transliteration in Latin script"
            />

            {latinSegments.length > 0 && (
              <div className="caption-publishing__segments">
                {latinSegments.map((segment, index) => (
                  <div key={`latin-${segment.startMs}-${index}`} className="caption-publishing__segment">
                    <div className="caption-publishing__segment-time">
                      {Math.floor((segment.startMs || 0) / 1000)}s -{" "}
                      {Math.floor((segment.endMs || 0) / 1000)}s
                    </div>
                    <div className="caption-publishing__segment-text">{segment.text}</div>
                  </div>
                ))}
              </div>
            )}

            <div className="caption-publishing__section-header caption-publishing__section-header--spaced">
              <h2 className="caption-publishing__section-title">
                4. Video script as per language in video
              </h2>
              <div className="caption-publishing__format-toggle caption-publishing__format-toggle--inline">
                <button
                  type="button"
                  className={`caption-publishing__format-btn${
                    nativeScriptFormat === "srt" ? " caption-publishing__format-btn--active" : ""
                  }`}
                  onClick={() => {
                    setNativeScriptFormat("srt");
                    onDownloadPhoneticSubtitle("srt");
                  }}
                  disabled={!phoneticSegments.length && !phoneticTranscript.trim()}
                >
                  .SRT (SubRip)
                </button>
                <button
                  type="button"
                  className={`caption-publishing__format-btn${
                    nativeScriptFormat === "vtt" ? " caption-publishing__format-btn--active" : ""
                  }`}
                  onClick={() => {
                    setNativeScriptFormat("vtt");
                    onDownloadPhoneticSubtitle("vtt");
                  }}
                  disabled={!phoneticSegments.length && !phoneticTranscript.trim()}
                >
                  .VTT (WebVTT)
                </button>
              </div>
            </div>
            <p className="caption-publishing__section-text">
              English-to-Hindi transliteration in Devanagari (phonetic typing). Same timing as the
              default script, written how the words sound in Hindi.
            </p>

            <textarea
              className="caption-publishing__transcript caption-publishing__transcript--phonetic"
              value={
                phoneticTranscript ||
                "Phonetic script will appear here once the API returns phoneticTranscript and phoneticSegments."
              }
              readOnly
              aria-label="Video script as per language in video"
            />

            {phoneticSegments.length > 0 && (
              <div className="caption-publishing__segments">
                {phoneticSegments.map((segment, index) => (
                  <div key={`phonetic-${segment.startMs}-${index}`} className="caption-publishing__segment">
                    <div className="caption-publishing__segment-time">
                      {Math.floor((segment.startMs || 0) / 1000)}s -{" "}
                      {Math.floor((segment.endMs || 0) / 1000)}s
                    </div>
                    <div className="caption-publishing__segment-text">
                      {segment.phoneticText ||
                        segment.phonetic ||
                        segment.textDevanagari ||
                        segment.text}
                    </div>
                  </div>
                ))}
              </div>
            )}

          </div>
        )}
      </div>
        </div>
      </div>
      </section>

      <CaptionPublishingLanding
        showPricing={showPricingSection}
        isLoggedIn={isLoggedIn}
        onFreePlanClick={handleFreePlanClick}
        onGetStartedPlan={handleGetStartedPlan}
        onTalkToSales={openContactModal}
      />

      <ContactUsModal show={showContactModal} onHide={closeContactModal} />

      <GoogleSignInModal
        show={showSignInModal}
        onHide={() => {
          pendingAuthActionRef.current = null;
          setShowSignInModal(false);
        }}
        onSuccess={handleSignInSuccess}
      />

      <CaptionPaymentModal
        show={showPaymentModal}
        plan={selectedPaymentPlan}
        onHide={() => {
          setShowPaymentModal(false);
          setSelectedPaymentPlan(null);
        }}
      />
    </div>
  );
};

export default CaptionPublishing;
